<template>
  <div class="deck-settings-modal">
    <div class="deck-settings-modal-container">
      <h2 class="title is-5">
        <b-button v-if="showBackButton" @click="backAndClose" type="is-white" icon-right="chevron-left" icon-pack="fas" icon-size="xl" />
        Customize your slide deck
      </h2>
      <div class="columns first-level-layout">
        <div class="column is-three-fifths">
          <div class="preview-slide-wrapper">
            <div class="preview-slide-container">
              <SlideShow
                @slideChange="(num) => trackPostHog('wizard_slide_change', { slide_num: num })"
                :deckSlides="mergedSlides"
                :metaData="mergedMetaData"
                :themeData="mergedThemeData"
                :eventSettings="{avatarStyle: localDeck.avatarStyle}"
              />
            </div>
            <b-notification v-if="!isEditor" :closable="false" has-icon icon="info-circle" icon-pack="fas" icon-size="is-small" size="is-small">
              You will be able to adjust these settings from the slide deck editor
            </b-notification>
          </div>
        </div>
        <div class="column is-two-fifths">
          <b-field horizontal label="Title">
            <b-input v-model="localDeck.joinSlideTitle" :disabled="!canEditTitle" />
          </b-field>
          <b-field horizontal label="Colors">
            <ColorSelector v-model="localDeck.colors" />
          </b-field>
          <b-field horizontal label="Fonts">
            <FontSelector v-model="localDeck.fonts" />
          </b-field>
          <b-field :horizontal="hasLogo" :label="hasLogo ? 'Logo' : ''">
            <LogoUploader
              v-model="localDeck.userLogoUrl"
              :class="{'is-aligned-left': hasLogo}"
              :compact="true"
            >
              <span class="logo-uploader-label">Upload Logo</span>
            </LogoUploader>
          </b-field>
          <div class="columns">
            <div class="column is-one-third">
              <b-field label="Avatars">
                <AvatarStyleSelector
                  v-model="localDeck.avatarStyle"
                  @upgrade="showUpgradeModal"
                  icon-wrapper-class="avatar-style-selector-icon-wrapper"
                  upgrade-intent="use different player avatar and join-in styling"
                />
              </b-field>
            </div>
            <div class="column is-one-third">
              <b-field label="Reactions">
                <ReactionSelector v-model="localDeck.reactions" @upgrade="showUpgradeModal" />
              </b-field>
            </div>
            <div class="column is-one-third">
              <b-field label="Reporting">
                <ReportingSelector
                  v-model="localDeck.reporting"
                  @upgrade="showUpgradeModal"
                  icon-wrapper-class="reporting-selector-icon-wrapper"
                  upgrade-intent="use advanced reporting options"
                />
              </b-field>
            </div>
          </div>
          <b-button
            @click="submitAndClose"
            :disabled="isButtonDisabled"
            type="is-primary"
            size="is-large"
            class="submit-button"
            data-cy="deck-settings-modal-submit-button"
          >
            {{ buttonLabel }}
          </b-button>
        </div>
      </div>
    </div>
    <div class="deck-settings-modal-footer">
      <template v-if="forceOpen">
        <div key="disabled" v-if="isDisabled()" @click="enable" class="text-button">
          Show this settings window each time I create a new slide deck
        </div>
        <div key="enabled" v-else-if="isReEnabled" @click="disable" class="text-button">
          This window will now show when you create a new deck. Click to disable
        </div>
      </template>
      <div v-else @click="startDisableAndClose" class="text-button">
        Don't show this settings window again
      </div>
    </div>
  </div>
</template>

<script>
import ColorSelector from '~/components/modals/deck-settings-modal/ColorSelector'
import FontSelector from '~/components/modals/deck-settings-modal/FontSelector'
import ReactionSelector from '~/components/modals/deck-settings-modal/ReactionSelector'
import AvatarStyleSelector from '~/components/decks/AvatarStyleSelector'
import ReportingSelector from '~/components/modals/deck-settings-modal/ReportingSelector'
import LogoUploader from '~/components/decks/LogoUploader'
import SlideShow from '~/components/modals/deck-settings-modal/SlideShow'
import { colorThemes, fontPairs } from '~/common/themeData'
import { deckSettingsModalOnboardingKey } from '~/common/decks'
import { reportingStates } from '~/common/reporting'
import { reactionStates } from '~/common/reactions'
import { mergeSlidesAndSettingsData } from '~/common/deck-settings'
import UpgradeModal from '~/components/modals/UpgradeModal'

const { RECAP_ONLY, RATINGS_ONLY, NONE, ALL } = reportingStates

export default {
  name: 'DeckSettingsModal',
  components: {
    SlideShow,
    LogoUploader,
    ReportingSelector,
    AvatarStyleSelector,
    ReactionSelector,
    FontSelector,
    ColorSelector
  },
  props: {
    themeData: {
      type: Object,
      required: true
    },
    metaData: {
      type: Object,
      required: true
    },
    eventSettings: {
      type: Object,
      required: true
    },
    slides: {
      type: Array,
      required: true
    },
    slideDeckName: {
      type: String,
      required: false,
      default: ''
    },
    forceOpen: {
      type: Boolean,
      default: false
    },
    buttonLabel: {
      type: String,
      default: 'See your slides →'
    },
    canEditTitle: {
      type: Boolean,
      default: true
    },
    showBackButton: {
      type: Boolean,
      default: false
    },
    isEditor: {
      type: Boolean,
      default: false
    },
    trackingData: {
      type: Object,
      default: () => ({
        domain: 'onboard',
        templateId: null,
        templateName: null
      })
    }
  },
  data () {
    return {
      initialized: false,
      isReEnabled: false,
      localDeck: {
        joinSlideTitle: '',
        colors: {},
        fonts: {},
        avatarStyle: null,
        reactions: null,
        reporting: null,
        userLogoUrl: ''
      }
    }
  },
  computed: {
    isButtonDisabled () {
      return this.localDeck.joinSlideTitle === ''
    },
    hasLogo () {
      return this.localDeck.userLogoUrl !== ''
    },
    mergedSlides () {
      return mergeSlidesAndSettingsData(this.slides, { joinSlideTitle: this.localDeck.joinSlideTitle })
    },
    mergedMetaData () {
      return {
        ...this.metaData,
        ...this.payload.metaData
      }
    },
    mergedThemeData () {
      return {
        ...this.themeData,
        ...this.payload.themeData
      }
    },
    firstSlideTitle () {
      return this.slides[0].content.title
    },
    payload () {
      let disableRatings, disableRecap

      switch (this.localDeck.reporting) {
        case RECAP_ONLY:
          disableRatings = true
          disableRecap = false
          break
        case RATINGS_ONLY:
          disableRatings = false
          disableRecap = true
          break
        case NONE:
          disableRatings = true
          disableRecap = true
          break
        case ALL:
        default:
          disableRatings = false
          disableRecap = false
      }

      const {
        joinSlideTitle = '',
        colors = {},
        fonts = {},
        reactions,
        avatarStyle = null,
        userLogoUrl = false
      } = this.localDeck

      const disableSounds = reactions === 'reactions-off'

      const deckName = joinSlideTitle !== this.firstSlideTitle ? joinSlideTitle : null

      return {
        deckName,
        joinSlideTitle,
        themeData: {
          colors,
          fonts
        },
        eventSettings: {
          disableRatings,
          disableRecap,
          disableSounds,
          avatarStyle
        },
        metaData: {
          userLogoUrl
        }
      }
    }
  },
  watch: {
    'localDeck.colors' () {
      if (this.initialized) {
        this.trackPostHog('wizard_setting_change', { setting_type: 'color' })
      }
    },
    'localDeck.fonts' () {
      if (this.initialized) {
        this.trackPostHog('wizard_setting_change', { setting_type: 'font' })
      }
    },
    'localDeck.avatarStyle' () {
      if (this.initialized) {
        this.trackPostHog('wizard_setting_change', { setting_type: 'avatar' })
      }
    },
    'localDeck.reactions' () {
      if (this.initialized) {
        this.trackPostHog('wizard_setting_change', { setting_type: 'reactions' })
      }
    },
    'localDeck.reporting' () {
      if (this.initialized) {
        this.trackPostHog('wizard_setting_change', { setting_type: 'reporting' })
      }
    },
    'localDeck.userLogoUrl' () {
      if (this.initialized) {
        this.trackPostHog('wizard_logo_upload')
      }
    }
  },
  mounted () {
    this.trackPostHog('wizard_start')

    const { disableRatings, disableRecap } = this.$props.eventSettings
    this.localDeck = {
      joinSlideTitle: this.firstSlideTitle || '',
      colors: colorThemes.find(theme => this.$props.themeData.colors?.name === theme.name) || colorThemes[0],
      fonts: fontPairs.find(pair => this.$props.themeData.fonts?.name === pair.name) || fontPairs[0],
      avatarStyle: this.$props.eventSettings.avatarStyle || null,
      reactions: this.$props.eventSettings.disableSounds ? reactionStates.OFF : reactionStates.ON,
      reporting: disableRatings && disableRecap ? NONE : disableRatings ? RECAP_ONLY : disableRecap ? RATINGS_ONLY : ALL,
      userLogoUrl: this.$props.metaData.userLogoUrl || ''
    }

    this.$nextTick(() => {
      this.initialized = true
    })

    const canCancel = this.$parent.canCancel === true ? ['escape'] : this.$parent.canCancel || []

    if (!canCancel.includes('escape')) {
      document.addEventListener('keydown', this.handleEscape)
    }
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    isDisabled () {
      return this.hasOnboarded(deckSettingsModalOnboardingKey)
    },
    startDisableAndClose () {
      this.$buefy.dialog.confirm({
        title: 'Disable settings window?',
        message: 'Would you like to close this deck customization helper and prevent it from opening on future decks? You can undo this setting in your account preferences.',
        cancelText: 'Cancel',
        confirmText: 'Do not show again',
        onConfirm: () => {
          this.disableAndClose()
        }
      })
    },
    disableAndClose () {
      this.disable()
      this.submitAndClose()
    },
    disable () {
      this.trackPostHog('wizard_disable')
      this.saveOnboarding(deckSettingsModalOnboardingKey)
    },
    enable () {
      this.isReEnabled = true
      this.saveOnboarding(deckSettingsModalOnboardingKey, true)
    },
    submitAndClose () {
      if (this.localDeck.joinSlideTitle !== this.firstSlideTitle) {
        this.trackPostHog('wizard_title_change', { title: this.localDeck.joinSlideTitle })
      }
      this.trackPostHog('wizard_complete')
      this.$emit('submit', this.payload)
      this.$parent.close()
    },
    backAndClose () {
      this.trackPostHog('wizard_back_button_click')
      this.$emit('back')
      this.$parent.close()
    },
    showUpgradeModal () {
      this.$buefy.modal.open({
        parent: this,
        fullScreen: false,
        component: UpgradeModal,
        props: {
          title: 'Upgrade your events',
          subtitle: 'Host up to 1000 people with custom event codes, data export, and even more ways to interact.',
          from_location: 'deck-settings-wizard',
          button_text: 'Upgrade your events'
        },
        canCancel: true,
        customClass: 'upgrade-modal swf-modal',
        width: 400,
        trapFocus: true
      })
    },
    handleEscape (evt) {
      if (evt.keyCode !== 27) {
        return
      }
      const openModals = document.querySelectorAll('.modal.is-active').length > 1

      if (!openModals) {
        this.$parent.close()
      }
    },
    trackPostHog (event, data) {
      const { domain, templateId, templateName } = this.trackingData || {}
      this.posthogCapture(`${domain}:${event}`, {
        from_deck_id: templateId,
        from_deck_name: templateName || this.slideDeckName,
        ...data
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/components';

$spacing: 30px;
$deck-border-color: #f1f2f4;

.deck-settings-modal {
  .deck-settings-modal-container {
    background-color: $white;
    padding: $spacing;
    border-radius: 6px;
  }

  h2.title {
    border-bottom: 1px solid $deck-border-color;
    margin: 0 (-$spacing) calc($spacing / 2);
    padding: 0 $spacing calc($spacing - 10px);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .columns.first-level-layout {
    margin-top: -15px;

    > .column {
      padding: $spacing $spacing calc($spacing / 2);

      &:first-child {
        padding-left: calc($spacing / 2);
      }

      &:last-child {
        padding-right: calc($spacing / 2);
      }
    }
  }

  .field {
    &:not(:last-child) {
      margin-bottom: 20px;
    }

    &.is-horizontal {
      align-items: center;
    }

    :deep(.field-label label.label) {
      font-size: 1rem;
    }
  }

  .submit-button {
    width: 100%;
    display: block;
    margin-top: $spacing * 1.5;
    border-radius: $border-radius;
  }

  .deck-settings-modal-footer {
    .text-button {
      color: $light-bg;
      cursor: pointer;
      text-decoration: underline;
      margin-top: 15px;
      font-size: .8rem;
      text-align: center;

      &:hover {
        color: $white;
      }
    }
  }

  .logo-uploader-label {
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :deep(.logo-uploader .image-prev) {
    height: 90px;
  }

  .tooltip-selector {
    width: 100%;
  }

  :deep(.avatar-style-selector-icon-wrapper) {
    padding: .2em;

    .tooltip-selector-option-icon.is-pro:not(.is-icon) {
      height: 40px;
    }
  }

  :deep(.reporting-selector-icon-wrapper) {
    .tooltip-selector-option-icon:not(.is-pro):not(.is-icon) {
      height: 48px;
    }
  }

  .preview-slide-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    position: relative;
    z-index: 1;

    &::before {
      content: '';
      display: block;
      position: absolute;
      background: #f8f9fc;
      top: -$spacing;
      left: -($spacing + 3px);
      right: -$spacing;
      bottom: -($spacing + 3px);
      z-index: -1;
      border-right: 1px solid $deck-border-color;
      border-bottom-left-radius: $border-radius-sm;
    }

    > * {
      z-index: 1;
    }
  }

  .preview-slide-container {
    width: 100%;
    border: 1px solid $border-color;
    border-radius: 8px;
  }

  .notification {
    color: #828282;
    font-size: .8em;
    border-radius: $border-radius-sm;
    background: #EFF1F6;
    padding: .5rem 1rem;
    width: 100%;

    :deep(.media-left) {
      margin-right: .5rem;
    }

    &:not(:last-child) {
      margin-bottom: 0;
    }
  }
}
</style>
